// Bundled, self-hosted line icons — replaces the network-loaded Material Symbols
// icon font. Each icon is a tiny inline SVG (24×24, 2px stroke, currentColor), so
// the app ships ZERO web fonts: nothing to fetch, works fully offline, precached
// by the service worker, and themeable via `color`.
//
// Why not the OS "system" icon set? There is no cross-platform system icon font
// on the web (SF Symbols is iOS-app-only, Segoe Fluent is Windows-only), so a
// bundled SVG set is the closest thing to native + the fastest to paint.
//
// Usage: `${icon('nfc')}` in a Lit template, or `${icon('nfc', 'icon')}` to fill
// a Material Web button's `slot="icon"`. Size follows the element's font-size
// (width/height:1em); Material Web hosts override it via their icon-size vars.
import { html, svg, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

// Inner geometry per icon. Stroke inherits currentColor + 2px width from <svg>;
// paths drawn as fills opt in explicitly (they still read as line-weight glyphs).
const paths = {
  add: svg`<path d="M12 5v14M5 12h14"/>`,
  arrow_forward: svg`<path d="M5 12h14M13 6l6 6-6 6"/>`,
  auto_awesome: svg`<path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1z"/><path d="M18.5 3.5l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9L16 6l1.9-.6z"/>`,
  badge: svg`<circle cx="12" cy="7.5" r="3.5"/><path d="M4.5 20c.6-3.6 3.7-5.5 7.5-5.5s6.9 1.9 7.5 5.5"/>`,
  call: svg`<path d="M5.5 3.5h2.4a1.5 1.5 0 0 1 1.4 1l.9 2.6a1.5 1.5 0 0 1-.5 1.7L8.2 10a12.5 12.5 0 0 0 5.8 5.8l1.2-1.5a1.5 1.5 0 0 1 1.7-.5l2.6.9a1.5 1.5 0 0 1 1 1.4v2.4a2 2 0 0 1-2.1 2A16.5 16.5 0 0 1 3.5 5.6a2 2 0 0 1 2-2.1z"/>`,
  check: svg`<path d="M5 13l4 4L19 7"/>`,
  close: svg`<path d="M6 6l12 12M18 6L6 18"/>`,
  collections_bookmark: svg`<rect x="3" y="8.5" width="18" height="11.5" rx="2.5"/><path d="M7 5h10"/>`,
  edit: svg`<path d="M4 20l1.2-4.2L16.4 4.6a2 2 0 0 1 2.8 0l.2.2a2 2 0 0 1 0 2.8L8.2 18.8 4 20z"/>`,
  flag: svg`<path d="M6 21V4h9l-1 3h5v9h-8l1-3H8"/>`,
  flip_to_back: svg`<path d="M10 10h9a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1z"/><path d="M6 14H5V5h9v1" stroke-dasharray="2.6 2.6"/>`,
  flip_to_front: svg`<path d="M5 5h9a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5z"/><path d="M10 19h9a1 1 0 0 0 1-1v-8" stroke-dasharray="2.6 2.6"/>`,
  // Two people: the front one drawn like `person_add` so the pair read as the
  // same family, the second as a half-head and a partial shoulder behind it.
  group: svg`<path d="M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M15.4 5.2a3.5 3.5 0 0 1 0 6.6"/><path d="M17 14.4a6.5 6.5 0 0 1 4.5 5.6"/>`,
  how_to_reg: svg`<path d="M10 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/><path d="M3.5 20a6.5 6.5 0 0 1 11.5-4.1"/><path d="M15 17.5l2 2 4-4"/>`,
  install_mobile: svg`<path d="M7 3h10a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/><path d="M12 7v6M9.5 10.5L12 13l2.5-2.5"/>`,
  ios_share: svg`<path d="M12 15V3.5M8 7l4-3.5L16 7"/><path d="M6 11H5.5A2.5 2.5 0 0 0 3 13.5v5A2.5 2.5 0 0 0 5.5 21h13a2.5 2.5 0 0 0 2.5-2.5v-5a2.5 2.5 0 0 0-2.5-2.5H18"/>`,
  language: svg`<path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z"/><path d="M3 12h18"/><path d="M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"/>`,
  link: svg`<path d="M10.2 13.8a4 4 0 0 0 5.6 0l2.8-2.8a4 4 0 1 0-5.6-5.6l-1 1"/><path d="M13.8 10.2a4 4 0 0 0-5.6 0l-2.8 2.8a4 4 0 1 0 5.6 5.6l1-1"/>`,
  mail: svg`<rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M3.8 7.5l6.9 5a2.2 2.2 0 0 0 2.6 0l6.9-5"/>`,
  menu: svg`<path d='M4 7h16M4 12h16M4 17h16'/>`,
  mic: svg`<path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3z"/><path d="M6 11a6 6 0 0 0 12 0"/><path d="M12 17v4M8.5 21h7"/>`,
  nfc: svg`<path d="M6.5 9a4.2 4.2 0 0 1 0 6"/><path d="M10 6.2a8 8 0 0 1 0 11.6"/><path d="M13.5 3.4a12 12 0 0 1 0 17.2"/>`,
  person_add: svg`<path d="M10 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/><path d="M3.5 20a6.5 6.5 0 0 1 13 0"/><path d="M19 6.5v5M16.5 9h5"/>`,
  photo_camera: svg`<path d="M3 9.5A2.5 2.5 0 0 1 5.5 7h1.6l1-1.9A2 2 0 0 1 9.9 4h4.2a2 2 0 0 1 1.8 1.1l1 1.9h1.6A2.5 2.5 0 0 1 21 9.5v8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5v-8z"/><circle cx="12" cy="13.5" r="3.5"/>`,
  place: svg`<path d="M12 21c-4-3.5-6.8-7-6.8-10.6a6.8 6.8 0 1 1 13.6 0C18.8 14 16 17.5 12 21z"/><circle cx="12" cy="10.2" r="2.6"/>`,
  print: svg`<path d="M7 8V4.5A1.5 1.5 0 0 1 8.5 3h7A1.5 1.5 0 0 1 17 4.5V8"/><path d="M7 17H5.5A2.5 2.5 0 0 1 3 14.5v-4A2.5 2.5 0 0 1 5.5 8h13a2.5 2.5 0 0 1 2.5 2.5v4a2.5 2.5 0 0 1-2.5 2.5H17"/><rect x="7" y="13.5" width="10" height="7" rx="1.5"/><path d="M17.2 11h.01"/>`,
  qr_code_2: svg`<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><path fill="currentColor" stroke="none" d="M14 14h3v3h-3zM18 18h3v3h-3z"/>`,
  save: svg`<rect x="3" y="4" width="18" height="11" rx="2.5"/><path d="M12 15.5v5M9.4 17.9L12 20.5l2.6-2.6"/>`,
  search: svg`<circle cx="11" cy="11" r="6.5"/><path d="M15.8 15.8L21 21"/>`,
  social: svg`<circle cx="17.8" cy="5.2" r="2.4"/><circle cx="6.2" cy="12" r="2.4"/><circle cx="17.8" cy="18.8" r="2.4"/><path d="M8.4 10.9l7.2-4.6M8.4 13.1l7.2 4.6"/>`,
  settings: svg`<path d="M4 7h8.5M17.5 7H20M4 12h2.5M11.5 12H20M4 17h9.5M18.5 17H20"/><circle cx="15" cy="7" r="2.5"/><circle cx="9" cy="12" r="2.5"/><circle cx="16" cy="17" r="2.5"/>`,
  swap_horiz: svg`<path d="M7 8h11l-3-3M17 16H6l3 3"/>`,
  sync: svg`<path d="M21 12a9 9 0 0 1-9 9c-2.5 0-4.8-1-6.4-2.7"/><path d="M3 12a9 9 0 0 1 9-9c2.5 0 4.8 1 6.4 2.7"/><path d="M18.4 3v3.7h-3.7M5.6 21v-3.7h3.7"/>`,
  stop: svg`<path d="M8.5 7.5h7a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z"/>`,
  upload: svg`<path d="M12 15V4M8 8l4-4 4 4"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/>`,
};

export type IconName = keyof typeof paths;

/** Gap between one icon's wake-up and the next, in ms. */
const STAGGER_MS = 90;
/** How many icons the stagger runs across before starting over, so a screen
 *  full of them finishes its greeting in about a second instead of trickling
 *  on for half a minute. */
const STAGGER_SLOTS = 12;

/**
 * Which icon is next in the stagger. Module-level and monotonic: icons render
 * in DOM order, so the count IS the running order, and each one wakes a moment
 * after the one before it.
 *
 * It wraps rather than growing, and it is never reset — an icon that appears
 * later (a sheet opening, a lazy component arriving) gets whatever slot is next,
 * which reads as the same unhurried rhythm rather than a fresh wave.
 */
let stagger = 0;

/**
 * The motion, carried by the icon itself.
 *
 * This ships INSIDE each SVG rather than in eleven components' stylesheets: the
 * icon set is one file, and motion is part of what an icon is here. A `<style>`
 * inside an SVG is not scoped to that SVG — it applies to the whole shadow root
 * it lands in — so every copy must be IDENTICAL, and anything per-instance (the
 * delay) has to travel as an inline style instead. That is the whole reason the
 * delay is set the way it is below; making it a rule here would mean the last
 * icon rendered silently set the delay for all of them.
 *
 * The hover and press rules need `!important` for the same reason: they have to
 * beat the inline animation the entrance uses.
 *
 * All of it sits behind `prefers-reduced-motion: no-preference`. Motion that
 * cannot be turned off is an accessibility failure, and this app's busiest
 * screen is a stranger's card opened by tapping a tag — an involuntary audience.
 */
const motionCSS = `
svg.ico { transform-origin: 50% 50%; }
@media (prefers-reduced-motion: no-preference) {
  svg.ico:hover, svg.ico:active { animation: ico-wake .5s cubic-bezier(.36,.07,.19,.97) both !important; }
}
/* The entrance is an INLINE animation (it carries a per-icon delay), and an
   inline style cannot be overridden by an ordinary rule — so switching it off
   for someone who asked for less motion has to be !important. Without this
   line, the one preference that matters would be the one setting ignored. */
@media (prefers-reduced-motion: reduce) {
  svg.ico { animation: none !important; }
}
@keyframes ico-wake {
  0%, 100% { transform: none; }
  15% { transform: rotate(-11deg) scale(1.07); }
  38% { transform: rotate(8deg) scale(1.05); }
  60% { transform: rotate(-5deg) scale(1.02); }
  82% { transform: rotate(2deg); }
}`;

/**
 * Render a bundled SVG icon. Pass `slot` to place it in a Material Web slot
 * (e.g. a button's `slot="icon"`). `fill:none` inline defeats Material Web's
 * `::slotted{fill:currentColor}` so line icons stay outlined inside buttons.
 *
 * Every icon wakes up once when it arrives — a small shake, one after another
 * down the screen, so the things a person can act on announce themselves — and
 * shakes again on hover or press. The entrance plays on INSERTION, so it
 * happens when someone lands on a screen and not on every re-render: Lit reuses
 * the element, and a CSS animation does not restart on an attribute change.
 */
export function icon(name: IconName, slot?: string): TemplateResult {
  const delay = (stagger++ % STAGGER_SLOTS) * STAGGER_MS;
  return html`<svg class="ico" viewBox="0 0 24 24" width="1em" height="1em" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true" slot=${ifDefined(slot)}
    style="fill:none;vertical-align:middle;flex:none;animation:ico-wake .5s cubic-bezier(.36,.07,.19,.97) ${delay}ms both"
    ><style>${motionCSS}</style>${paths[name]}</svg>`;
}
